import { supabase } from '@/integrations/supabase/client';
import { nanoid } from 'nanoid';

export interface FileUploadResult {
  path: string;
  name: string;
  type: string;
  size: number;
  uploadedAt: string;
}

// ✅ PHASE 4: Expanded file type support
const ALLOWED_TYPES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Sanitize filename for storage: Unicode-safe, no special chars
 * @param filename Original filename
 * @returns Normalized filename (max 80 chars)
 */
export function sanitizeFileName(filename: string): string {
  const normalized = filename.normalize('NFKD');
  const safe = normalized.replace(/[^\w.-]+/g, '_');
  return safe.slice(0, 80);
}

export function generateUniqueFileName(originalName: string): string {
  const sanitized = sanitizeFileName(originalName);
  const ext = sanitized.split('.').pop() || '';
  const nameWithoutExt = sanitized.replace(`.${ext}`, '');
  const uuid = crypto.randomUUID();
  return `${uuid}-${nameWithoutExt}.${ext}`;
}

export async function uploadStakeholderFile(
  stakeholderId: string,
  file: File
): Promise<FileUploadResult> {
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error(`File type ${file.type} not allowed. Supported: images, PDF, DOC/DOCX`);
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds 5MB limit`);
  }

  const uniqueFileName = generateUniqueFileName(file.name);
  const filePath = `${stakeholderId}/${uniqueFileName}`;

  if (import.meta.env.DEV) {
    console.debug(`[uploadStakeholderFile] Uploading to: ${filePath}`);
  }

  const { data, error } = await supabase.storage
    .from('stakeholder-attachments')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type
    });

  if (error) {
    console.error('[uploadStakeholderFile] Upload failed:', error);
    throw error;
  }

  if (import.meta.env.DEV) {
    console.debug(`[uploadStakeholderFile] Upload successful`, {
      stakeholderId,
      fileName: file.name,
      path: data.path,
      size: file.size
    });
  }

  // ✅ PHASE 4: Verify file exists using signed URL (more reliable than .list for fresh uploads)
  try {
    const { data: signedUrlData, error: verifyError } = await supabase.storage
      .from('stakeholder-attachments')
      .createSignedUrl(filePath, 60); // 60 seconds validity

    if (verifyError || !signedUrlData?.signedUrl) {
      console.error('[uploadStakeholderFile] File verification failed:', verifyError);
      throw new Error('Upload succeeded but file verification failed. Check RLS policies.');
    }
    
    if (import.meta.env.DEV) {
      console.debug('[uploadStakeholderFile] File verified via signed URL');
    }
  } catch (verifyErr) {
    console.error('[uploadStakeholderFile] Verification error:', verifyErr);
    throw new Error('Upload succeeded but file verification failed. Check RLS policies.');
  }

  return {
    path: data.path,
    name: file.name,
    type: file.type,
    size: file.size,
    uploadedAt: new Date().toISOString()
  };
}

export async function getSignedFileUrl(
  path: string,
  expiresIn: number = 3600
): Promise<string> {
  const { data, error } = await supabase.storage
    .from('stakeholder-attachments')
    .createSignedUrl(path, expiresIn);

  if (error) throw error;
  if (!data?.signedUrl) throw new Error('Failed to generate signed URL');

  return data.signedUrl;
}

export async function deleteStakeholderFile(path: string): Promise<void> {
  const { error } = await supabase.storage
    .from('stakeholder-attachments')
    .remove([path]);

  if (error) throw error;
}

export async function deleteStakeholderFiles(paths: string[]): Promise<void> {
  if (paths.length === 0) return;

  const { error } = await supabase.storage
    .from('stakeholder-attachments')
    .remove(paths);

  if (error) throw error;
}

export async function deleteAllStakeholderFiles(stakeholderId: string): Promise<void> {
  const { data, error: listError } = await supabase.storage
    .from('stakeholder-attachments')
    .list(stakeholderId, { limit: 1000 });

  if (listError) throw listError;

  const paths = (data || []).map(obj => `${stakeholderId}/${obj.name}`);
  
  if (paths.length > 0) {
    await deleteStakeholderFiles(paths);
  }
}
